import { ReactNode, useCallback, useEffect, useState } from 'react';
import { AuthContext, ClientUser } from './auth';
import { useToast } from '@/hooks/use-toast';
import { InsertUser, User } from '@/api-client/autogenerated';
import {
  getCurrentUser,
  loginUser,
  logoutUser,
  registerUser,
  requestPasswordReset,
} from '@/lib/api/auth';
import { useLocation } from 'wouter';
import { ENABLE_REGISTRATION } from '@/utils/env-setup';

interface AuthProviderProps {
  children: ReactNode;
}

function transformUser(u: User & { LiveToken?: string }): ClientUser {
  return {
    ...u,
    id: u.Id,
    role: u.Role?.RoleName.toLowerCase(),
    liveToken: u.LiveToken,
  };
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<ClientUser | null>(null);
  const [allowedStates, setAllowedStates] = useState<string[]>([]);
  const [isFullUser, setIsFullUser] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const [location, navigate] = useLocation();

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await getCurrentUser();
      setUser(transformUser(response));
      setIsFullUser((response.AccountUserRelationships || []).some((r) => Boolean(r.FullAccount)));
      setAllowedStates(
        response.UserStateSubscriptions?.filter((s) => s.Status === 'active').map(
          (s) => s.StateCode,
        ) || [],
      );
    } catch {
      setUser(null);
      setIsFullUser(false);
      setAllowedStates([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // List of public routes that don't require authentication
    const publicRoutes = ['/auth', '/demo-signup', '/accept-invite'];
    const isPublicRoute = publicRoutes.some(
      (route) => location === route || location.startsWith(route + '/'),
    );

    if (!isPublicRoute || location === '/') {
      // Always fetch current user for non-public routes OR the root path
      // This ensures authenticated users on "/" get redirected to dashboard
      fetchCurrentUser();
    } else {
      // On other public routes, just set loading to false
      setIsLoading(false);
    }
  }, [fetchCurrentUser, location]);

  const login = async (username: string, password: string): Promise<ClientUser> => {
    try {
      const response = await loginUser(username, password);
      const transformed = transformUser(response);
      setUser(transformed);
      setIsFullUser((response.AccountUserRelationships || []).some((r) => Boolean(r.FullAccount)));
      setAllowedStates(
        response.UserStateSubscriptions?.filter((s) => s.Status === 'active').map(
          (s) => s.StateCode,
        ) || [],
      );
      return transformed;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message || 'Login failed');
      }
      throw error;
    }
  };

  const register = async (params: InsertUser) => {
    if (!ENABLE_REGISTRATION) throw new Error('Registration is currently disabled');
    try {
      const response = await registerUser(params);
      const transformed = transformUser(response);
      setUser(transformed);
      setIsFullUser((response.AccountUserRelationships || []).some((r) => Boolean(r.FullAccount)));
      setAllowedStates(
        response.UserStateSubscriptions?.filter((s) => s.Status === 'active').map(
          (s) => s.StateCode,
        ) || [],
      );
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message || 'Registration failed');
      }
    }
  };

  const logout = async () => {
    try {
      console.debug('Attempting logout');
      const response = await logoutUser();
      console.debug('Logout response status:', response.status);
      setUser(null);
      setAllowedStates([]);
      toast({
        title: 'Logged out',
        description: 'You have been successfully logged out.',
      });
      const navigated = navigate('/');
      console.debug('navigate("/") returned:', navigated);
    } catch (error) {
      console.error('Logout failed', error);
      if (error instanceof Error) {
        toast({
          title: 'Logout failed',
          description: error.message || 'Could not log out properly.',
          variant: 'destructive',
        });
      }
    }
  };

  const forgotPassword = async (usernameOrEmail: string) => {
    await requestPasswordReset(usernameOrEmail);
    toast({
      title: 'Password reset sent',
      description: 'Check your email for a temporary password to sign in.',
    });
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    isFullUser,
    login,
    register,
    logout,
    requestPasswordReset: forgotPassword,
    allowedStates,
    refetchUser: fetchCurrentUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
